import { CommsEscalationReason, type CommsEscalationMetadata } from "@/src/models/comms-escalation";
import { getPersonName, type NameFields } from "@/src/models/person";
import type { SchedulingEscalationReason, SchedulingRecommendation } from "@/src/models/scheduling-recommendation";

const UNKNOWN_PROVIDER = "N/A";

function outreachNotePrefix(
  referringProvider: NameFields | null | undefined,
  receivingProvider: NameFields | null | undefined
): string {
  const referringProviderName = getPersonName(referringProvider, false) || UNKNOWN_PROVIDER;
  const receivingProviderName = getPersonName(receivingProvider, false) || UNKNOWN_PROVIDER;

  return `Patient referred by Dr. ${referringProviderName} to Dr. ${receivingProviderName}`;
}

export function buildUshvMkcOutreachAssignmentNote(
  referringProvider: NameFields | null | undefined,
  receivingProvider: NameFields | null | undefined
): string {
  return `${outreachNotePrefix(referringProvider, receivingProvider)} - Valerie Health outreaching patient`;
}

const GENERIC_SMS_INELIGIBLE_REASON = "Patient not eligible for SMS scheduling";

// Ops-approved wording. Other escalation reasons have no approved wording and fall back to the generic text.
const SMS_INELIGIBLE_REASON_TEXT: Partial<Record<SchedulingEscalationReason, string>> = {
  "Age": "Patient not eligible for SMS scheduling due to being under 18 years old",
  "Language": "Patient not eligible for SMS scheduling due to unsupported language",
  "Upcoming appointment": "Patient not eligible for SMS scheduling due to appointment in less than 3 days",
};

/**
 * Why the patient won't get SMS outreach, or null when they are eligible or eligibility isn't known yet.
 * `isEligible` is the persisted shouldRecommendForSchedulingInReferralTask verdict that also gates
 * shouldAutoSendInitialOutreach.
 */
export function resolveUshvMkcSmsIneligibleReason(
  recommendation: Pick<SchedulingRecommendation, "isEligible" | "escalationReason"> | null | undefined
): string | null {
  if (recommendation?.isEligible !== false) return null;

  return (
    (recommendation.escalationReason && SMS_INELIGIBLE_REASON_TEXT[recommendation.escalationReason]) ||
    GENERIC_SMS_INELIGIBLE_REASON
  );
}

export function buildUshvMkcSmsIneligibleAssignmentNote(
  referringProvider: NameFields | null | undefined,
  receivingProvider: NameFields | null | undefined,
  ineligibleReason: string
): string {
  return `${outreachNotePrefix(referringProvider, receivingProvider)} - ${ineligibleReason} - Valerie Health`;
}

// "3 attempts" duplicates MKC's cadence in configuration/sms-follow-up-cadence.ts; nothing validates it.
export function buildUshvMkcOutreachNoResponseNote(
  referringProvider: NameFields | null | undefined,
  receivingProvider: NameFields | null | undefined
): string {
  return `${outreachNotePrefix(referringProvider, receivingProvider)} - Valerie Health made 3 attempts, patient did not respond. Please call the patient.`;
}

// Excludes invalid_phone_number deliberately: it raises the same task type, but nothing was ever
// sent, so "made 3 attempts" would be false and ops has approved no other wording.
export function hasUshvMkcCommsEscalationNote(metadata: CommsEscalationMetadata): boolean {
  return metadata.reason === CommsEscalationReason.CONVERSATION_AUTO_CLOSED;
}

export function resolveUshvMkcCommsEscalationNote(
  metadata: CommsEscalationMetadata,
  referringProvider: NameFields | null | undefined,
  receivingProvider: NameFields | null | undefined
): string | null {
  return hasUshvMkcCommsEscalationNote(metadata)
    ? buildUshvMkcOutreachNoResponseNote(referringProvider, receivingProvider)
    : null;
}
